import { isAxiosError } from "axios"
import type { CancelablePromise } from "../client"
import { OpenAPI } from "../client"
import { request as __request } from "../client/core/request"
import {
  type ExReceptionDto,
  type ExReceptionsUpdateDto as ExReceptionsUpdateDTO,
  getExternalReceptionById,
  type ReceptionDraftResponseDto,
  updateExternalReceptionById,
} from "../client/pz"

const pzRequestOptions = () => ({
  baseURL: OpenAPI.BASE || "",
  headers: {
    Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
  },
  throwOnError: true as const,
})

const genericPzUpdateError =
  "Nie udało się zapisać szkicu PZ. Spróbuj ponownie później."

const safePzUpdateErrorCodes = new Set([
  "invalid_request",
  "document_not_found",
  "document_not_draft",
  "warehouse_invalid",
  "item_invalid",
  "location_required",
  "supplier_invalid",
  "vat_invalid",
])

type PzRequestErrorBody = {
  error: string
  code?: string
}

/** Error shape expected by the existing document autosave workflow. */
export class PzRequestError extends Error {
  readonly body: PzRequestErrorBody
  readonly status?: number
  readonly code?: string

  constructor(body: PzRequestErrorBody, status?: number) {
    super(body.error)
    this.name = "PzRequestError"
    this.body = body
    this.status = status
    this.code = body.code
  }
}

function getSafePzErrorBody(payload: unknown): PzRequestErrorBody | null {
  if (!payload || typeof payload !== "object") return null

  const candidate = payload as { error?: unknown; code?: unknown }
  if (typeof candidate.error !== "string" || !candidate.error.trim()) {
    return null
  }

  const code =
    typeof candidate.code === "string" && candidate.code.trim()
      ? candidate.code
      : undefined
  return code ? { error: candidate.error, code } : { error: candidate.error }
}

function normalizePzUpdateError(error: unknown): PzRequestError {
  if (isAxiosError(error)) {
    const status = error.response?.status
    if (status !== undefined && status >= 400 && status < 500) {
      const safeBody = getSafePzErrorBody(error.response?.data)
      if (safeBody?.code && safePzUpdateErrorCodes.has(safeBody.code)) {
        return new PzRequestError(safeBody, status)
      }
    }
    return new PzRequestError({ error: genericPzUpdateError }, status)
  }

  return new PzRequestError({ error: genericPzUpdateError })
}

export type InitExReceptionDraftDTO = {
  warehouse_id: string
  contragent_id?: string
  supplier?: string
  description?: string
  based_document?: string
  partia?: string
}

export type ExReceptionDraftResponseDTO = {
  id: string
  number: string
  status: string
  created_at: string
  updated_at?: string | null
  confirmed_at?: string | null
}

export const ExReceptionDraftsService = {
  initExReceptionDraft: (
    payload: InitExReceptionDraftDTO,
  ): CancelablePromise<ExReceptionDraftResponseDTO> =>
    __request(OpenAPI, {
      method: "POST",
      url: "/v1/external-receptions/drafts",
      body: payload,
      mediaType: "application/json",
    }),

  deleteExReceptionDraft: (id: string): CancelablePromise<void> =>
    __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/external-receptions/{id}",
      path: { id },
    }),
}

export const ExReceptionsDraftUpdateService = {
  updateExReceptionByID: (
    id: string,
    data: ExReceptionsUpdateDTO,
  ): Promise<ReceptionDraftResponseDto> =>
    updateExternalReceptionById({
      ...pzRequestOptions(),
      body: data,
      path: { id },
    })
      .then((response) => response.data)
      .catch((error: unknown) => {
        throw normalizePzUpdateError(error)
      }),
}

export const ExReceptionDetailService = {
  getExReceptionByID: (id: string): Promise<ExReceptionDto> =>
    getExternalReceptionById({
      ...pzRequestOptions(),
      path: { id },
    }).then((response) => response.data),
}
